import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { useInstall } from "../components/InstallPrompt";
import { Loader2, Download, CheckCircle2, MonitorDown } from "lucide-react";

const DEMO = [
  { label: "Administrateur", email: "nkabongo@aims.ac.rw", password: "Admin@FHI360" },
  { label: "Épidémiologiste de terrain", email: "epi1@fhi360.org", password: "Epi@2026" },
  { label: "Superviseur provincial", email: "superviseur.nordkivu@fhi360.org", password: "Epi@2026" },
  { label: "Analyste SI", email: "analyste.si@fhi360.org", password: "Epi@2026" },
];

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { canInstall, install, installed, ios } = useInstall();
  const [showHow, setShowHow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.landing || "/dashboard");
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <Logo size={30} tone="light" />
          <span className="font-semibold text-base tracking-tight">Stride</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl font-semibold tracking-tight leading-tight">{t("Système de Surveillance Épidémiologique")}</h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            {t("Standardisez les rapports quotidiens de terrain, calculez automatiquement les indicateurs de surveillance et générez les rapports hebdomadaires par Zone de Santé et Province · RDC.")}
          </p>
        </div>
        <p className="relative z-10 text-xs text-white/60">{t("Pilier Surveillance / Épidémiologie de Terrain")}</p>
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#FFF 1px, transparent 1px), linear-gradient(90deg, #FFF 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      </div>

      <div className="flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-sm">
          <div className="flex justify-end mb-2">
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden text-xs font-semibold" data-testid="lang-switcher-login">
              <button onClick={() => setLang("fr")} data-testid="lang-fr-login"
                className={`px-3 py-1.5 transition-colors ${lang === "fr" ? "bg-[#F05A28] text-white" : "text-slate-500 hover:bg-slate-100"}`}>FR</button>
              <button onClick={() => setLang("en")} data-testid="lang-en-login"
                className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-[#F05A28] text-white" : "text-slate-500 hover:bg-slate-100"}`}>EN</button>
            </div>
          </div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">{t("Connexion")}</h2>
          <p className="text-sm text-slate-500 mt-1">{t("Accédez à votre espace de surveillance.")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">{t("Adresse e-mail")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="vous@fhi360.org" />
            </div>
            <div>
              <Label htmlFor="password">{t("Mot de passe")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-md bg-[#F05A28] hover:bg-[#D84A1B]">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("Se connecter")}
            </Button>
          </form>

          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("Comptes de démonstration")}</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.email} data-testid={`demo-${d.email}`}
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  className="text-left text-xs border border-slate-200 rounded-md px-3 py-2 hover:border-[#F05A28] hover:bg-[#FFF3EE] transition-colors">
                  <span className="font-semibold text-slate-700 block">{t(d.label)}</span>
                  <span className="text-slate-400 truncate block">{d.email}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-md border border-[#FDDCCF] bg-[#FFF8F5] p-4" data-testid="login-install">
            <div className="flex items-start gap-3">
              <MonitorDown className="h-5 w-5 text-[#F05A28] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{t("Installer Stride sur cet appareil")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t("Icône sur le bureau ou l'écran d'accueil, fenêtre dédiée, saisie et rapports même sans réseau.")}</p>
                {installed ? (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="login-installed"><CheckCircle2 className="h-4 w-4" />{t("Stride est déjà installé sur cet appareil.")}</p>
                ) : canInstall ? (
                  <Button type="button" onClick={install} className="mt-3 h-9 rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="login-install-btn">
                    <Download className="h-4 w-4 mr-1.5" />{t("Installer Stride maintenant")}
                  </Button>
                ) : (
                  <>
                    <button type="button" onClick={() => setShowHow((v) => !v)} className="mt-2 text-xs font-semibold text-[#F05A28] hover:underline" data-testid="login-install-how">
                      {t(showHow ? "Masquer les étapes" : "Comment installer ?")}
                    </button>
                    {showHow && (
                      <ol className="mt-2 space-y-1 text-xs text-slate-600 list-decimal pl-4" data-testid="login-install-steps">
                        {ios ? (
                          <>
                            <li>{t("Ouvrez cette page dans Safari.")}</li>
                            <li>{t("Touchez le bouton Partager (carré avec une flèche).")}</li>
                            <li>{t("Choisissez « Sur l'écran d'accueil » puis « Ajouter ».")}</li>
                          </>
                        ) : (
                          <>
                            <li>{t("Ordinateur (Chrome / Edge) : cliquez sur l'icône « Installer » à droite de la barre d'adresse, ou menu ⋮ › « Installer Stride ».")}</li>
                            <li>{t("Android (Chrome) : menu ⋮ › « Ajouter à l'écran d'accueil » ou « Installer l'application ».")}</li>
                            <li>{t("Stride s'ouvre ensuite depuis son icône, comme un logiciel classique.")}</li>
                          </>
                        )}
                      </ol>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
